import React, { CSSProperties, ReactNode, useState } from 'react';
import { Handle, Position, Node, HandleProps, IsValidConnection, NodeResizer, OnResize } from '@xyflow/react';
import { HidroDXEditor } from '../edit';
import { IconButton, MenuItem, Popover, Tooltip, Typography } from '@mui/material';
import { ColorPicker } from '../modals';
import { Edit } from '@mui/icons-material';

export type BaseNodeData = {
  lock?: boolean;
  markdown?: string;
  children?: ReactNode;
  style: CSSProperties;
  handles: HandleProps[];
  onChange?: (newNode: Node<BaseNodeData>) => void;
}

const defaultDivHeight = 200;
const defaultDivWidth = 200;

const handleSize = 10;
const targetColor = "rgb(2, 41, 53)";
const sourceColor = "rgb(0, 153, 102)";

export function BaseNode(nodeData: Node<BaseNodeData>) {

  const [nodeStyle, setNodeStyle] = useState<BaseNodeData>(nodeData.data);
  const [editRef, setEditRef] = useState<HTMLButtonElement>();
  const [colorPickerEl, setColorPickerEl] = useState<HTMLButtonElement>();


  const handleOpenColorPicker = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    setColorPickerEl(ev.currentTarget);
  };

  const handleResize: OnResize = (_, params) => {
    const newStyle = {
      ...nodeStyle,
      style: {
        ...nodeStyle.style,
        width: params.width,
        height: params.height,
      }
    };

    setNodeStyle(newStyle);
    if (nodeStyle.onChange) {
      nodeStyle.onChange({
        ...nodeData,
        data: newStyle,
      });
    }
  };

  const validateConnection: IsValidConnection = (conn) => {
    if (conn.source === conn.target) return false;
    return true;
  };

  const style = nodeStyle.style;
  const text = nodeStyle.markdown;
  const handles = nodeStyle.handles;
  const children = nodeStyle.children;
  const locked = nodeStyle.lock ?? false;
  const divWidth = Number(nodeStyle.style.width) ?? defaultDivWidth;
  const divHeight = Number(nodeStyle.style.height) ?? defaultDivHeight;

  const topHandles: HandleProps[] = handles.filter(val => val.position === Position.Top);
  const bottomHandles: HandleProps[] = handles.filter(val => val.position === Position.Bottom);
  const leftHandles: HandleProps[] = handles.filter(val => val.position === Position.Left);
  const rightHandles: HandleProps[] = handles.filter(val => val.position === Position.Right);

  const containerStyle: React.CSSProperties = {
    width: 200,
    height: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: style.backgroundColor ?? '#e0e0e0',
    ...style,
  };

  return (
    <>

      {editRef &&
        <Popover
          open={Boolean(editRef)}
          anchorEl={editRef}
          onClose={() => setEditRef(undefined)}
          sx={{ width: "556px" }}
        >
          {/* Seletor de Cor */}
          <MenuItem disableRipple sx={{ flexDirection: 'row', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Cor de Fundo
            </Typography>
            <IconButton onClick={handleOpenColorPicker}>
              <div style={{
                width: "15px",
                height: "15px",
                border: "1px solid black",
                backgroundColor: nodeStyle.style.backgroundColor,
              }} />
            </IconButton>
          </MenuItem>

          <HidroDXEditor
            startText={text}
            onChange={(text) => {
              const newStyle = {
                ...nodeStyle,
                markdown: text,
              };

              setNodeStyle(newStyle);

              if (nodeStyle.onChange) nodeStyle.onChange({
                ...nodeData,
                data: newStyle
              });
            }}
          />
        </Popover >
      }

      {/* Title BG color */}
      {
        colorPickerEl &&
        <ColorPicker
          open={Boolean(colorPickerEl)}
          element={colorPickerEl}
          initialColor={nodeStyle.style.backgroundColor}
          onClose={() => setColorPickerEl(undefined)}
          onColor={(color) => {
            const newStyle = {
              ...nodeStyle,
              style: {
                ...nodeStyle.style,
                backgroundColor: color,
              }
            };

            setNodeStyle(newStyle);

            if (nodeStyle.onChange) nodeStyle.onChange({
              ...nodeData,
              data: newStyle
            });
          }}
        />
      }

      <div style={{ position: "relative" }}>
        {/* Handle Top */}
        {topHandles.map((val, idx) => {
          val.style = {
            ...val.style,
            backgroundColor: val.type === 'target' ? targetColor : sourceColor,
            borderRadius: val.type === 'source' ? undefined : "2px",
            width: handleSize,
            height: val.type === 'source' ? handleSize : handleSize / 1.5,
            left: ((idx + 1) * (divWidth / (topHandles.length + 1)))
          };
          return <Handle
            key={`top-${idx}`}
            id={val.id}
            type={val.type}
            position={val.position}
            style={{
              ...val.style,
              zIndex: 1000,
            }}
            isConnectable={!locked}
            isValidConnection={validateConnection}
          />;
        })}

        {/* Handle Bottom */}
        {bottomHandles.map((val, idx) => {
          val.style = {
            ...val.style,
            backgroundColor: val.type === 'target' ? targetColor : sourceColor,
            borderRadius: val.type === 'source' ? undefined : "2px",
            width: handleSize,
            height: val.type === 'source' ? handleSize : handleSize / 1.5,
            left: ((idx + 1) * (divWidth / (bottomHandles.length + 1)))
          };

          return <Handle
            key={`bottom-${idx}`}
            id={val.id}
            type={val.type}
            position={val.position}
            style={{
              ...val.style,
              zIndex: 1000,
            }}
            isConnectable={!locked}
            isValidConnection={validateConnection}
          />;
        })}

        {/* Left Handles */}
        {leftHandles.map((val, idx) => {
          val.style = {
            ...val.style,
            backgroundColor: val.type === 'target' ? targetColor : sourceColor,
            borderRadius: val.type === 'source' ? undefined : "2px",
            width: val.type === 'source' ? handleSize : handleSize / 1.5,
            height: handleSize,
            top: ((idx + 1) * (divHeight / (leftHandles.length + 1)))
          };

          return <Handle
            key={`left-${idx}`}
            id={val.id}
            type={val.type}
            position={val.position}
            style={{
              ...val.style,
              zIndex: 1000,
            }}
            isConnectable={!locked}
            isValidConnection={validateConnection}
          />;
        })}

        {/* Right Handles */}
        {rightHandles.map((val, idx) => {
          val.style = {
            ...val.style,
            backgroundColor: val.type === 'target' ? targetColor : sourceColor,
            borderRadius: val.type === 'source' ? undefined : "2px",
            width: val.type === 'source' ? handleSize : handleSize / 1.5,
            height: handleSize,
            top: ((idx + 1) * (divHeight / (rightHandles.length + 1)))
          };

          return <Handle
            key={`right-${idx}`}
            id={val.id}
            type={val.type}
            position={val.position}
            style={{
              ...val.style,
              zIndex: 1000,
            }}
            isConnectable={!locked}
            isValidConnection={validateConnection}
          />;
        })}


        <NodeResizer
          color='#000a'
          isVisible={!locked}
          minWidth={5}
          minHeight={5}
          onResize={handleResize}
        />

        {/* Editar o Título */}
        {!locked &&
          <div style={{
            transform: 'scale(0.6)',
            transformOrigin: 'center',
            position: 'absolute',
            top: 0,
            right: 0,
            width: "38px",
            height: '35px',
            backgroundColor: "#000",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "15px",
            zIndex: 100,
          }}>
            <Tooltip title="Editar Título" >
              <IconButton
                onClick={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  setEditRef(ev.currentTarget);
                }}
                size='small'
                sx={{ color: "#FFF" }}
              >
                <Edit fontSize='small' />
              </IconButton>
            </Tooltip>
          </div>
        }

        <div style={{
          padding: "15px",
          ...containerStyle,
          boxSizing: "border-box",
          height: divHeight,
          width: divWidth,
        }}>

          {children}
          {text && <HidroDXEditor
            key={`${text.length}-${text.slice(Math.max(text.length - 50, 0), text.length)}`}
            readOnly
            startText={text}
          />}
        </div>
      </div>
    </>
  );
}

